<script setup lang="ts">
import { useHowtoPage } from '~/composables/content/how-to'

const { pageData } = await useHowtoPage()

const title = computed(
  () => pageData.value?.seo?.metaTitle || pageData.value?.title || ''
)
const description = computed(
  () =>
    pageData.value?.seo?.metaDescription || pageData.value?.description || ''
)
const image = computed(
  () => pageData.value?.seo?.metaImage?.url || pageData.value?.image?.url || ''
)

/**
 * Attach SEO meta to the page
 */
useSeoMeta({
  title: title,
  description: description,
  ogTitle: title,
  ogDescription: description,
  ogImage: image,
  ogImageWidth: '1200',
  ogImageHeight: '630',
  ogImageType: 'image/jpeg',
  ogType: 'website',
  twitterTitle: title,
  twitterDescription: description,
  twitterImage: image,
  twitterCard: 'summary_large_image',
})

definePageMeta({
  layout: 'base',
})
</script>

<template>
  <article class="prose prose-invert max-w-4xl container mx-auto px-4">
    <NuxtImg
      v-if="pageData?.image?.url"
      :alt="pageData.title"
      :src="pageData.image.url"
      class="w-full object-contain rounded-lg"
      provider="strapi"
      placeholder
    />

    <div class="mt-15.5 flex items-center">
      <!-- <Icon name="belong:i-date" /> -->
      <img alt="" src="/images/external/other/icon-date.svg" />
      <UiDateTime
        v-if="pageData.date"
        :value="pageData.date"
        class="ml-1.5 text-lg uppercase tracking-wide text-[#969696]"
      />
    </div>

    <h1>{{ pageData?.title || pageData?.seo?.metaTitle || '' }}</h1>

    <template v-for="component in pageData.dynamicContent" :key="component.id">
      <V-HTML
        v-if="component.__component === 'dynamic-content.content'"
        :html="component.content"
      ></V-HTML>

      <ol
        class="steps"
        v-if="component.__component === 'dynamic-content.step-group'"
      >
        <li v-for="step in component.step" :key="component.id + step.id">
          <p class="text-base tracking-wide text-[#FFB0F0] py-[0.1875rem] m-0">
            {{ step.title }}
          </p>
          <V-HTML v-if="step.content" :html="step.content"></V-HTML>
        </li>
      </ol>
    </template>
  </article>
</template>

<style scoped>
@reference "tailwindcss";
.steps {
  @apply list-none space-y-4 pl-0 ml-0 relative;

  > li {
    @apply pl-30;
  }

  /**
    instruction https://css-tricks.com/everything-you-need-to-know-about-the-gap-after-the-list-marker/
   */
  > li::before {
    content: 'STEP ' counter(list-item);
    @apply text-[#FFB0EF] bg-[#151515] absolute left-0 border border-solid border-[#FFB0EF] text-lg rounded-[1.25rem] px-2 min-w-21.5 text-center;
  }

  > li::after {
    @apply bg-[#FFB0F0] absolute w-0.25 h-full top-0 left-11 -z-1;
    content: '';
  }
}
</style>
